import {
    setMics,
    setLoading,
    setCameraPermission, setMicPermission,
    startDeviceMonitoring,
    startMonitoringCameraPermission, startMonitoringMicrophonePermission,
    setIsMonitoringCamera, setIsMonitoringMicrophone
} from './mediaControlSlice';
import { listenerMiddleware } from '../../../store/middleware';
import { refreshDevices } from '../../../store/thunks';



let isDeviceChangeListenerActive = false;
let onBrowserDeviceChangeHandler = null;

const IS_MEDIA_DEVICES_SUPPORTED = !!(navigator.mediaDevices && navigator.mediaDevices.enumerateDevices);


/**
 * @typedef {'camera' | 'microphone'} PermissionNameType
 * @typedef {object} ActiveBrowserListener
 * @property {PermissionStatus | null} statusObject - The browser's PermissionStatus object.
 * @property {(() => void) | null} onchangeHandler - The currently attached onchange handler.
 */

/**
 * Module-level store to ensure only one browser event listener
 * is active per permission type. This is crucial for stability.
 * @type {Object.<PermissionNameType, ActiveBrowserListener>}
 */
const activeBrowserListeners = {
    camera: { statusObject: null, onchangeHandler: null },
    microphone: { statusObject: null, onchangeHandler: null },
};

/**
 * Helper effect function to set up and manage a permission monitor.
 * This encapsulates the core logic for querying and listening to permission changes.
 * It ensures that for a given permissionName, only one browser 'onchange' listener is active.
 *
 * @param {PermissionNameType} permissionName
 * @param {import('@reduxjs/toolkit').ListenerEffectAPI<any, any>} listenerApi
 */
async function setupPermissionMonitorEffect(permissionName, listenerApi) {
    const { dispatch, signal } = listenerApi;

    // Action creator for dispatching permission updates
    let permissionActionCreator = null;
    if (permissionName === 'camera') {
        permissionActionCreator = setCameraPermission;
    } else if (permissionName === 'microphone') {
        permissionActionCreator = setMicPermission;
    }

    if (!permissionActionCreator) {
        console.error(`[ListenerMiddleware] No action creator found for permissionName: ${permissionName}`);
        return null; // Return null instead of a no-op function
    }

    // Clear any pre-existing onchange handler for this permission type
    if (activeBrowserListeners[permissionName].statusObject && activeBrowserListeners[permissionName].onchangeHandler) {
        activeBrowserListeners[permissionName].statusObject.onchange = null;
        console.log(`[ListenerMiddleware] Cleared previous onchange for ${permissionName}.`);
    }
    activeBrowserListeners[permissionName] = { statusObject: null, onchangeHandler: null };

    if (!navigator.permissions) {
        console.error(`[ListenerMiddleware] Permissions API not supported for ${permissionName}.`);
        dispatch(permissionActionCreator('unsupported'));
        return null;
    }

    try {
        console.log(`[ListenerMiddleware] Setting up monitor for ${permissionName}.`);
        const permissionStatus = await navigator.permissions.query({ name: permissionName });



        activeBrowserListeners[permissionName].statusObject = permissionStatus;

        // Dispatch initial state
        dispatch(permissionActionCreator(permissionStatus.state));
        console.log(`[ListenerMiddleware] Initial permission state for ${permissionName}: ${permissionStatus.state}`);

        const onChangeCallback = () => {
            console.log(`[ListenerMiddleware] onChangeCallback for ${permissionName} FIRED. Current state: ${permissionStatus.state}, Signal aborted: ${signal.aborted}`);

            console.log(`[ListenerMiddleware] Permission state for ${permissionName} changed to: ${permissionStatus.state}`);
            dispatch(permissionActionCreator(permissionStatus.state));
            dispatch(refreshDevices());
        };

        permissionStatus.onchange = onChangeCallback;
        activeBrowserListeners[permissionName].onchangeHandler = onChangeCallback;

        // Return cleanup function
        return () => {
            console.log(`[ListenerMiddleware] Cleaning up ${permissionName} permission monitor.`);
            if (permissionStatus) {
                permissionStatus.onchange = null;
            }
            // Clear the record only if this specific handler was the one attached
            if (activeBrowserListeners[permissionName].onchangeHandler === onChangeCallback) {
                activeBrowserListeners[permissionName] = { statusObject: null, onchangeHandler: null };
            }
        };


    } catch (error) {
        console.error(`[ListenerMiddleware] Error querying/monitoring ${permissionName} permission:`, error.message);
        dispatch(permissionActionCreator('error'));
        return null;
    }
}







// --- Device Listener ---
listenerMiddleware.startListening({
    actionCreator: startDeviceMonitoring,
    effect: async (action, listenerApi) => {
        console.log('[DeviceListener] startDeviceMonitoring effect triggered.');
        if (!IS_MEDIA_DEVICES_SUPPORTED) {
            listenerApi.dispatch(setLoading(false));
            listenerApi.dispatch(setMics([])); // etc. for empty state
            return;
        }

        if (isDeviceChangeListenerActive) {
            console.log('[DeviceListener] Device change listener already active. Performing initial scan.');
            // Perform a scan if already active but triggered again.
            listenerApi.dispatch(refreshDevices());
            return; // Avoid re-attaching
        }

        // Perform initial scan
        listenerApi.dispatch(refreshDevices());

        // Define and attach the browser's 'devicechange' event listener
        onBrowserDeviceChangeHandler = async () => {
            console.log('[DeviceListener] Browser `devicechange` event detected!');
            listenerApi.dispatch(refreshDevices());
        };

        navigator.mediaDevices.addEventListener('devicechange', onBrowserDeviceChangeHandler);
        isDeviceChangeListenerActive = true;
        console.log('[DeviceListener] Browser `devicechange` listener attached.');

        // The effect can return a cleanup function for when this listener instance is stopped
        // (e.g., if `stopDeviceMonitoring` is implemented to cancel this listener instance)
        // However, a simple "stop" action is often easier.
        // For now, this sets up a persistent listener.
    }
});




listenerMiddleware.startListening({
    actionCreator: startMonitoringCameraPermission,
    predicate: (action, currentState) => {
        // Only run if not already monitoring
        return !currentState.mediaControls.isMonitoringCamera;
    },
    effect: async (action, listenerApi) => {
        const { dispatch, signal } = listenerApi;

        // ✅ Properly dispatch the action
        dispatch(setIsMonitoringCamera(true));

        console.log('[ListenerMiddleware] Camera monitoring effect triggered.');

        try {
            const cleanup = await setupPermissionMonitorEffect('camera', listenerApi);

            // ✅ Always return cleanup function (setupPermissionMonitorEffect should handle this)
            return () => {
                console.log('[ListenerMiddleware] Camera listener cleanup triggered.');
                dispatch(setIsMonitoringCamera(false));
                if (cleanup) cleanup();
            };
        } catch (error) {
            console.error('[ListenerMiddleware] Error in camera permission monitoring:', error);
            dispatch(setIsMonitoringCamera(false));
        }
    },
});

// --- Microphone Permission Listener ---
listenerMiddleware.startListening({
    actionCreator: startMonitoringMicrophonePermission,
    predicate: (action, currentState) => {
        // Only run if not already monitoring
        return !currentState.mediaControls.isMonitoringMicrophone;
    },
    effect: async (action, listenerApi) => {
        const { signal, dispatch } = listenerApi;
        console.log('[ListenerMiddleware] Microphone monitoring effect triggered.');

        try {
            const cleanup = await setupPermissionMonitorEffect('microphone', listenerApi);

            return () => {
                console.log('[ListenerMiddleware] Microphone listener cleanup triggered.');
                dispatch(setIsMonitoringMicrophone(false));
                if (cleanup) cleanup();
            };
        } catch (error) {
            console.error('[ListenerMiddleware] Error in microphone permission monitoring:', error);
            dispatch(setIsMonitoringMicrophone(false));
        }
    },
});

